import React, { useState, useEffect, useCallback } from "react";
import { Platform } from "react-native";
import { createBottomTabNavigator } from "@react-navigation/bottom-tabs";
import { House, Search, CirclePlus, MessageCircle, User } from "lucide-react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useSelector } from "react-redux";

import HomeScreen from "../screens/HomeScreen";
import BrowseScreen from "../screens/BrowseScreen";
import ProfileScreen from "../screens/ProfileScreen";
import { useColors } from "../hooks/useColors";
import SellScreen from "../screens/SellScreen";
import { ChatListScreen } from "../screens/ChatListScreen";
import MainHeader from "../components/MainHeader";
import { navigationRef } from "../services/NavigationService";
import { useGuestMode } from "../contexts/GuestModeContext";
import { RootState } from "../store";

const Tab = createBottomTabNavigator();

// Custom header component that gets the active tab name
// Uses navigationRef event listener for immediate updates
function DynamicMainHeader() {
  const [activeTabName, setActiveTabName] = useState("Home");
  
  const updateActiveTab = useCallback(() => {
    try {
      if (!navigationRef.isReady()) return;
      
      // Get navigation state directly from ref
      const state = navigationRef.getState();
      if (!state) return;
      
      // Navigate through nested navigation state to find active tab
      // The structure is: Root -> MainApp -> Main (Tab Navigator) -> active tab
      const rootRoute = state.routes?.[state.index];
      if (rootRoute?.name === 'MainApp' && rootRoute.state) {
        const mainAppRoute = rootRoute.state.routes?.[rootRoute.state.index];
        if (mainAppRoute?.name === 'Main' && mainAppRoute.state) {
          const activeTab = mainAppRoute.state.routes?.[mainAppRoute.state.index];
          if (activeTab?.name) {
            setActiveTabName(activeTab.name);
          }
        }
      }
    } catch (error) {
      // Navigation not ready or state structure different, keep default
    }
  }, []);
  
  useEffect(() => {
    // Update immediately on mount
    updateActiveTab();
    
    // Listen for navigation state changes - this fires immediately when tabs change
    const unsubscribe = navigationRef.addListener('state', updateActiveTab);
    
    return unsubscribe;
  }, [updateActiveTab]);
  
  return <MainHeader title={activeTabName} />;
}

function MainTabNavigator() {
  const colors = useColors();
  const insets = useSafeAreaInsets();
  const isAuthenticated = useSelector((state: RootState) => !!state.auth.user);
  const { requireAuth } = useGuestMode();
  
  // Account for iOS home indicator and Android navigation bar (Samsung, etc.)
  // Use actual insets on both platforms to handle various device navigation bars
  const bottomPadding = Math.max(insets.bottom, 10);
  const tabBarHeight = 60 + bottomPadding;

  return (
    <Tab.Navigator
      screenOptions={{
        tabBarActiveTintColor: colors.primary,
        tabBarInactiveTintColor: colors.textLight,
        tabBarShowLabel: true,
        tabBarLabelPosition: 'below-icon',
        tabBarStyle: {
          backgroundColor: colors.tabBarBackground,
          borderTopColor: colors.tabBarBorder,
          borderTopWidth: 1,
          height: tabBarHeight,
          paddingBottom: bottomPadding,
          paddingTop: 6,
        },
        tabBarLabelStyle: {
          fontSize: 11,
          fontWeight: '500',
          marginTop: 2,
        },
        header: () => <DynamicMainHeader />,
        // Keep screens mounted to prevent layout shift when switching tabs
        // This preserves data/state and avoids re-fetching and loading states
        lazy: false,
        unmountOnBlur: false,
        // Ensure dark background for tab screen containers
        sceneContainerStyle: {
          backgroundColor: colors.background,
        },
      }}
    >
      <Tab.Screen
        name="Home"
        component={HomeScreen}
        options={{
          tabBarLabel: 'Home',
          tabBarIcon: ({ color, size }) => (
            <House size={size} color={color} />
          ),
        }}
      />
      <Tab.Screen
        name="Browse"
        component={BrowseScreen}
        options={{
          tabBarLabel: 'Browse',
          tabBarIcon: ({ color, size }) => (
            <Search size={size} color={color} />
          ),
        }}
      />
      <Tab.Screen
        name="Sell"
        component={SellScreen}
        options={{
          tabBarLabel: 'Sell',
          tabBarIcon: ({ color, size }) => (
            <CirclePlus size={size} color={color} />
          ),
        }}
        listeners={{
          tabPress: (e) => {
            if (!isAuthenticated) {
              e.preventDefault();
              requireAuth('sell items');
            }
          },
        }}
      />
      <Tab.Screen
        name="Chats"
        component={ChatListScreen}
        options={{
          tabBarLabel: 'Chats',
          tabBarIcon: ({ color, size }) => (
            <MessageCircle size={size} color={color} />
          ),
        }}
        listeners={{
          tabPress: (e) => {
            if (!isAuthenticated) {
              e.preventDefault();
              requireAuth('access chats');
            }
          },
        }}
      />
      <Tab.Screen
        name="Profile"
        component={ProfileScreen}
        options={{
          tabBarLabel: 'Profile',
          tabBarIcon: ({ color, size }) => (
            <User size={size} color={color} />
          ),
        }}
        listeners={{
          tabPress: (e) => {
            if (!isAuthenticated) {
              e.preventDefault();
              requireAuth('access your profile');
            }
          },
        }}
      />
    </Tab.Navigator>
  );
}

export default MainTabNavigator;
